import { CustomPIXIComponent } from 'react-pixi-fiber'
import { Container, Sprite, Text, DEG_TO_RAD } from "pixi.js"
import { Tween, Easing } from "@tweenjs/tween.js"
import { Spine } from 'pixi-spine'
import Timeout from 'macguffin/Utils/Timeout'

import { createTween } from "utils/TweenHelper"

import { BADGE_OFFSET } from "game/components/reels/symbols/CometSymbol"

import { Loader } from "pixi.js"
const { resources } = Loader.shared

const { abs } = Math

const COMET_WILD_DELAY = 150
const COMET_WILD_DURATION = 500

const COMET_WILD_MOVEMENT_DELAY = 300

const COMET_WILD_TURN_DURATION = 150
const COMET_WILD_TURN_DELAY = 50

const SCALE = 1.5

const BADGE_SCALE = 0.75

const WILD_SCALE = 2.2
const WILD_OFFSET = {
    x: 0,
    y: 0.17
}
const WILD_OPACITY_DURATION = 500

const TEXT_FORMAT = {
    fontFamily: "WorkSans-Bold",
    fontSize: 48,
    fill: 0xffffff,
    align: "center",
    strokeThickness: 4,
    stroke: "black",
    miterLimit: 2,
    padding: 10
}

//precompute the directions and their respective reflections
const DIRECTIONS = {
    WRsw: 45 * DEG_TO_RAD,
    WRnw: 135 * DEG_TO_RAD,
    WRne: 225 * DEG_TO_RAD,
    WRse: 315 * DEG_TO_RAD
}

const REFLECTIONS = {
    [DIRECTIONS.WRsw]: {
        WRnw: 135 * DEG_TO_RAD,
        WRse: -45 * DEG_TO_RAD
    },
    [DIRECTIONS.WRnw]: {
        WRsw: 45 * DEG_TO_RAD,
        WRne: 225 * DEG_TO_RAD
    },
    [DIRECTIONS.WRne]: {
        WRnw: 135 * DEG_TO_RAD,
        WRse: 315 * DEG_TO_RAD
    },
    [DIRECTIONS.WRse]: {
        WRne: 225 * DEG_TO_RAD,
        WRsw: 405 * DEG_TO_RAD
    }
}

const HALF_ROTATION_RADS = 180 * DEG_TO_RAD
const FULL_ROTATION_RADS = 360 * DEG_TO_RAD

function getRotation(currentRotation, direction) {
    if (currentRotation) {
        const reflection = REFLECTIONS[currentRotation]
        return reflection && reflection[direction] || DIRECTIONS[direction]
    } else {
        return DIRECTIONS[direction]
    }
}

let badgeTextureCache
function getBadgeTexture() {
    if (badgeTextureCache) {
        return badgeTextureCache
    }

    badgeTextureCache = resources["badge"].texture
    return badgeTextureCache
}

let wildTextureCache
function getWildTexture() {
    if (wildTextureCache) {
        return wildTextureCache
    }

    wildTextureCache = resources["wildText"].texture
    return wildTextureCache
}

let spineDataCache
function getSpineData() {
    if (spineDataCache) {
        return spineDataCache
    }

    spineDataCache = resources["meteor"].spineData
    return spineDataCache
}

function createMoveTween(displayObject, props) {
    const { positions, width, height, index, onStart } = props
    const { x, y, turnsLeft, symbol } = positions

    const reelIndex = x - 1
    const symbolIndex = y - 1

    const spine = displayObject.getChildAt(0)
    const rotation = getRotation(spine.rotation, symbol)
    const rotationDifference = abs(rotation - spine.rotation)

    spine.state.setAnimation(0, "trans")
    spine.state.addAnimation(0, "reel", true, 0)

    const onStartExtended = () => {
        const rotateTween = new Tween(spine)
            .easing(Easing.Quadratic.InOut)
            .delay(COMET_WILD_TURN_DELAY)
            .onComplete(() => {
                if (spine.rotation > FULL_ROTATION_RADS) {
                    spine.rotation -= FULL_ROTATION_RADS
                }
            })

        if (spine.rotation && rotationDifference !== HALF_ROTATION_RADS) {
            rotateTween.to({ rotation }, COMET_WILD_TURN_DURATION)
        } else {
            rotateTween.onStart(() => {
                spine.rotation = rotation
            })
        }

        rotateTween.start()
        onStart && onStart()
    }

    const onComplete = () => {
        updateTurnsLeft(displayObject, turnsLeft)
        displayObject.isTweening = false
    }

    const target = {
        x: width * reelIndex + (width * 0.5),
        y: height * symbolIndex + (height * 0.5),
        delay: COMET_WILD_MOVEMENT_DELAY,
        duration: COMET_WILD_DURATION,
        easing: Easing.Quartic.InOut,
        onStart: onStartExtended,
        onComplete
    }

    tweenWildOpacity(displayObject, 0)

    displayObject.isTweening = true
    return createTween(displayObject, target)
}

function createSymbol(displayObject, props) {
    const { positions, width, height, resuming } = props
    const { oldx, oldy, turnsLeft, symbol } = positions
    const reelIndex = oldx - 1
    const symbolIndex = oldy - 1

    const spineData = getSpineData()

    const spine = new Spine(spineData)
    spine.state.setAnimation(0, "reel", true)
    spine.stateData.defaultMix = 0.1

    if (resuming) {
        spine.rotation = DIRECTIONS[symbol]
    }

    displayObject.addChild(spine)

    displayObject.x = width * reelIndex + (width * 0.5)
    displayObject.y = height * symbolIndex + (height * 0.5)

    const badgeTexture = getBadgeTexture()
    const badge = new Sprite(badgeTexture)
    badge.anchor.set(0.5)
    displayObject.addChild(badge)

    const wildTexture = getWildTexture()
    const wild = new Sprite(wildTexture)
    wild.anchor.set(0.5)
    displayObject.addChild(wild)

    const text = new Text(turnsLeft, TEXT_FORMAT)
    text.anchor.set(0.5)
    badge.addChild(text)
    badge.visible = turnsLeft
}

function updateSymbolPosition(displayObject, props) {
    const { width, height, positions } = props
    const { x, y } = positions
    const reelIndex = x - 1
    const symbolIndex = y - 1

    displayObject.x = width * reelIndex + (width * 0.5)
    displayObject.y = height * symbolIndex + (height * 0.5)
}

function updateSymbolSize(displayObject, props) {
    const { width, height } = props

    const spine = displayObject.getChildAt(0)

    spine.width = width * SCALE
    spine.scale.y = spine.scale.x

    if (displayObject.children.length > 1) {
        const badge = displayObject.getChildAt(1)
        const { x: badgeX, y: badgeY } = BADGE_OFFSET
        badge.scale.x = spine.scale.x * BADGE_SCALE
        badge.scale.y = spine.scale.y * BADGE_SCALE
        badge.position.set(width * badgeX, height * badgeY)

        const wild = displayObject.getChildAt(2)
        const { x: wildX, y: wildY } = WILD_OFFSET
        wild.scale = spine.scale
        wild.position.set(width * wildX, height * wildY)
    }
}

function updateTurnsLeft(displayObject, newTurnsLeft) {
    const badge = displayObject.getChildAt(1)
    badge.visible = newTurnsLeft
    const label = badge.getChildAt(0)
    label.text = newTurnsLeft
}

function tweenWildOpacity(displayObject, alpha = 1) {
    const wild = displayObject.getChildAt(2)
    const wildTween = new Tween(wild)
        .to({ alpha }, WILD_OPACITY_DURATION)
        .easing(Easing.Quartic.InOut)
        .start()
}

function tweenWildScale(displayObject) {
    const wild = displayObject.getChildAt(2)

    const halfDuration = WILD_OPACITY_DURATION * 0.5
    const currentScale = wild.scale

    const upTween = new Tween(wild.scale)
        .to({ x: currentScale.x * WILD_SCALE, y: currentScale.y * WILD_SCALE }, halfDuration)
        .easing(Easing.Quartic.Out)

    const downTween = new Tween(wild.scale)
        .to({ x: currentScale.x, y: currentScale.y }, halfDuration)
        .easing(Easing.Elastic.Out)

    upTween.chain(downTween).start()
}

function getSpine(displayObject) {
    return displayObject.getChildAt(0)
}

//CUSTOM REACT-PIXI CLASS
export default CustomPIXIComponent({

    customDisplayObject(props) {
        return new Container()
    },

    customApplyProps(displayObject, oldProps, newProps) {
        if (!displayObject.children.length) {
            createSymbol(displayObject, newProps)
        }

        const spine = getSpine(displayObject)
        spine.state.timeScale = !newProps.paused ? 1 : 0

        if (newProps.width !== oldProps.width || newProps.height !== oldProps.height) {
            updateSymbolPosition(displayObject, newProps)
            updateSymbolSize(displayObject, newProps)
        }

        if (newProps.positions !== oldProps.positions) {
            if (oldProps.positions) {
                //offset the comet animations so they don't all move in sync
                const delayTimeout = new Timeout(() => {
                    const moveTween = createMoveTween(displayObject, newProps)
                    moveTween.start()
                }, COMET_WILD_DELAY * newProps.index)
            } else {
                updateSymbolPosition(displayObject, newProps)
            }
        }

        if (newProps.reelStoppedIndex !== oldProps.reelStoppedIndex) {
            if ((newProps.positions.x - 1) === newProps.reelStoppedIndex) {
                tweenWildOpacity(displayObject, 1)
                tweenWildScale(displayObject)
            }
        }

        if (newProps.positions && oldProps.positions) {
            if (newProps.positions.turnsLeft > oldProps.positions.turnsLeft) {
                updateTurnsLeft(displayObject, newProps.positions.turnsLeft)
            }
        }

        if (newProps.respinSummary && newProps.respinSummary !== oldProps.respinSummary) {
            spine.state.setAnimation(0, "die")
            tweenWildOpacity(displayObject, 0)
        }

    }
}, 'Comet')